import type { TEvent } from '@/lib/mock';
import { thermalColor } from '@/lib/temp';
import { untilLabel } from '@/lib/useNow';
import { cn } from '@/lib/utils';

/**
 * A ticket's time-forward spine: events sit on the left (past), the SLA deadline
 * is marked ahead on the right, and a live "now" indicator moves between them.
 */
export function TimelineSpine({
  createdAt,
  slaDueAt,
  events,
  now,
  heat,
  compact = false,
}: {
  createdAt: number;
  slaDueAt: number;
  events: TEvent[];
  now: number;
  heat: number;
  compact?: boolean;
}) {
  const span = Math.max(1, slaDueAt - createdAt);
  const pos = (t: number) => Math.max(0, Math.min(1, (t - createdAt) / span)) * 100;
  const nowPos = pos(now);
  const breached = now > slaDueAt;
  const color = thermalColor(heat);

  return (
    <div className={cn('w-full', compact ? 'py-1' : 'py-2')}>
      <div className="relative h-6">
        {/* base line */}
        <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-border" />
        {/* elapsed line, warmth-colored */}
        <div
          className="absolute left-0 top-1/2 h-[2px] -translate-y-1/2 rounded-full"
          style={{ width: `${nowPos}%`, background: color }}
        />

        {/* event dots */}
        {events.map((e, i) => (
          <span
            key={i}
            title={e.label}
            className="absolute top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-muted-foreground/70"
            style={{ left: `${pos(e.at)}%` }}
          />
        ))}

        {/* now indicator */}
        <span
          className="animate-now absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${nowPos}%` }}
        >
          <span
            className="block h-3 w-3 rounded-full ring-2 ring-background"
            style={{ background: color }}
          />
        </span>

        {/* SLA deadline marker */}
        <span className="absolute right-0 top-1/2 -translate-y-1/2">
          <span
            className={cn(
              'block h-3.5 w-0.5 rounded-full',
              breached ? 'bg-temp-ember' : 'bg-foreground/40',
            )}
          />
        </span>
      </div>

      {!compact && (
        <div className="mt-1 flex items-center justify-between font-mono text-[10px] text-muted-foreground">
          <span>opened</span>
          <span
            className={breached ? 'font-medium text-temp-ember' : 'font-medium text-foreground'}
          >
            SLA {breached ? 'breached' : 'in'} {untilLabel(slaDueAt, now)}
          </span>
        </div>
      )}
    </div>
  );
}
